/**
 * PermissionChangeGate
 *
 * Shell-mounted watcher that reacts when THIS user's effective
 * permissions change mid-session (an admin edited their role / access
 * while they were signed in).
 *
 * Flow:
 *   1. Backend emits a bare `{ type: 'permissions_changed' }` frame on
 *      the per-user channel `user_{schema}_{user_id}` (no PII in the
 *      payload — see apps/auth_integration realtime emit).
 *   2. We refetch /auth/me/ and diff it against the in-memory user held
 *      by AuthContext (classifyPermissionChange).
 *   3. Show a modal:
 *        - upgrade   → dismissible ("Refresh now" / "Later"). The new
 *                      access only lights up after a refresh, so "Later"
 *                      is safe — nothing new is enabled meanwhile.
 *        - downgrade → BLOCKING. The session may still surface actions
 *                      the user can no longer perform, so we force a
 *                      refresh (which re-fetches a fresh /me/ and
 *                      re-gates every component).
 *
 * We deliberately do NOT push the fresh user into AuthContext live —
 * dozens of components hold derived, permission-gated state, so a hard
 * reload is the reliable resync (same reasoning as impersonation teardown
 * and the update banner).
 */

import { useCallback, useRef, useState } from 'react';
import { Shield, RefreshCw } from 'lucide-react';

import { useAuth } from './useAuth';
import { authService } from './authService';
import { classifyPermissionChange, type PermissionChangeDirection } from './permissionDiff';
import { useRealtimeMessage, type RealtimeMessage } from '@/shared/hooks/useRealtimeSocket';
import { useTranslation } from '@/core/i18n';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/theme/components/ui/alert-dialog';

type ActiveChange = Exclude<PermissionChangeDirection, 'none'>;

export function PermissionChangeGate() {
  const { t } = useTranslation();
  const { user, isAuthenticated, impersonation } = useAuth();
  const [change, setChange] = useState<ActiveChange | null>(null);
  // Guards a fetch already in flight so a burst of events (editing
  // several permissions in one save fans out several frames) doesn't
  // stack /me/ refetches.
  const inFlightRef = useRef(false);

  const applyDirection = useCallback((direction: ActiveChange) => {
    setChange((prev) => {
      // A downgrade always wins and can never be downgraded back to an
      // upgrade prompt; an already-open upgrade isn't re-shown.
      if (prev === 'downgrade') return prev;
      if (prev && direction === 'upgrade') return prev;
      return direction;
    });
  }, []);

  useRealtimeMessage(
    useCallback(
      (msg: RealtimeMessage) => {
        if (msg.type !== 'permissions_changed') return;
        if (!isAuthenticated || !user) return;
        // Impersonation runs its own session lifecycle; leave it alone.
        if (impersonation) return;
        // Already forced — nothing a further event can escalate to.
        if (change === 'downgrade') return;
        if (inFlightRef.current) return;

        inFlightRef.current = true;
        void (async () => {
          try {
            const fresh = await authService.syncCurrentUser();
            const direction = classifyPermissionChange(user, fresh);
            if (direction !== 'none') applyDirection(direction);
          } catch {
            // Safe bias: if we can't confirm the new set, assume the
            // worst (a possible downgrade) and force a refresh rather
            // than risk leaving stale, over-privileged UI in place.
            applyDirection('downgrade');
          } finally {
            inFlightRef.current = false;
          }
        })();
      },
      [user, isAuthenticated, impersonation, change, applyDirection],
    ),
  );

  if (!change) return null;

  const isDowngrade = change === 'downgrade';

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleDismiss = () => {
    // Only reachable for an upgrade.
    setChange(null);
  };

  return (
    <AlertDialog open onOpenChange={(open) => {
      // Escape / programmatic close: allowed to dismiss an upgrade,
      // ignored for a downgrade (the modal stays until refresh).
      if (!open && !isDowngrade) handleDismiss();
    }}>
      <AlertDialogContent
        onEscapeKeyDown={(e) => { if (isDowngrade) e.preventDefault(); }}
      >
        {/* Neutral copy in both cases — we never reveal which permissions
            were added or removed, only that an administrator changed them
            and the page must be reloaded. The only difference between an
            upgrade and a downgrade is whether "Later" is offered. */}
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" aria-hidden="true" />
            {t('permissions.change.title', 'Your permissions have changed')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t(
              'permissions.change.body',
              'An administrator updated your permissions. Reload the page to continue with your current access.',
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {!isDowngrade && (
            <AlertDialogCancel onClick={handleDismiss}>
              {t('permissions.change.later', 'Later')}
            </AlertDialogCancel>
          )}
          <AlertDialogAction onClick={handleRefresh} className="gap-1.5">
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            {t('permissions.change.reloadNow', 'Reload now')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
